import type {ComponentProps} from 'react';

/**
 * The only `<img>` a report asset may be drawn with.
 *
 * **This is what keeps the PDF export from losing its photos and logos.**
 * `html2canvas` does not reuse the elements on screen: it clones the document
 * and re-fetches each image with `crossOrigin="anonymous"`, because a canvas
 * drawn from a non-CORS image is tainted and `toDataURL` throws. If the
 * on-screen `<img>` fetched the same URL WITHOUT `crossOrigin`, the browser has
 * already cached it as a non-CORS resource and answers the second request from
 * that entry — which fails the CORS check instantly, without ever reaching S3.
 * html2canvas then skips the image and draws the sheet without it, so the
 * report exports with empty slots while the screen looks perfect.
 *
 * Loading as CORS from the start means one kind of cache entry exists and both
 * readers can use it. Diagnosing this is genuinely hard, because the failure is
 * served from cache in about a millisecond and looks identical to a missing
 * bucket rule: correcting the bucket changes nothing until the cache is
 * bypassed.
 *
 * **A component rather than a `crossOrigin` constant, deliberately.** The
 * constant version of this was advisory — nothing obliged a new image to import
 * it, and in the same pull request that introduced it a logo inside a table
 * cell was added without it and would have exported blank. An invariant that
 * every call site has to remember is not an invariant.
 *
 * So it is held in three places, none of which rely on remembering: the props
 * type omits `crossOrigin` so passing it does not compile, the attribute is
 * applied after the spread so it cannot be overridden anyway, and `paintSheets`
 * refuses the export at run time if a remote image reaches the capture without
 * it. The first two are this file's job; the third catches a bare `<img>` that
 * never came through here at all.
 *
 * Use it for every REMOTE report asset — logos and photos, wherever they are
 * drawn, on the sheet or on the authoring canvas, because both read the same
 * bucket. A `data:` signature needs nothing and stays a plain `<img>`.
 *
 * **It does mean the storage MUST allow GET from this site's origin.** With
 * `crossOrigin` set, a bucket without that rule fails the image on screen too,
 * rather than only in the export. That is the right trade — a visibly broken
 * photo beats a client-facing PDF with a hole in it — but it makes the bucket's
 * CORS configuration load-bearing for ordinary viewing. Before this reaches a
 * new environment, check it:
 *
 *   aws s3api get-bucket-cors --bucket <attachments-bucket>
 *
 * and make sure `GET` and the web origin are both in a rule.
 */
export function ReportImage(props: Omit<ComponentProps<'img'>, 'crossOrigin'>) {
  // `crossOrigin` LAST, and omitted from the props type. Spreading after it
  // would let a call site pass the attribute and win, which is a default
  // dressed up as a guarantee -- the same advisory nothing the constant this
  // replaced already was.
  return <img {...props} crossOrigin="anonymous" />;
}
